import React from "react";

/**
 * Bundled mascot SVGs for the Flappy game. Each mascot is rendered inside a
 * 100×100 viewBox so they can be displayed at any size. Wings are drawn as
 * separate elements so the parent can animate them via Framer Motion.
 *
 * Adding a new preset:
 *   1. Append { id, label, render } to MASCOT_PRESETS.
 *   2. The render function must accept { wingPhase: 0|1 } and return JSX.
 */

export const MASCOT_PRESETS = [
  { id: "cupcake", label: "Cupcake" },
  { id: "pizza", label: "Pizza" },
  { id: "coffee", label: "Café" },
  { id: "burger", label: "Burger" },
  { id: "bottle", label: "Bouteille" },
];

/**
 * MascotSvg — render a preset mascot inside a square SVG.
 * @param preset    one of "cupcake" | "pizza" | "coffee" | "burger" | "bottle"
 * @param size      pixel size (square)
 * @param wingPhase 0 (wings down) or 1 (wings up)
 */
export function MascotSvg({ preset = "cupcake", size = 48, wingPhase = 0 }) {
  const renderer = RENDERERS[preset] || RENDERERS.cupcake;
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      style={{ overflow: "visible" }}
      aria-hidden
    >
      {renderer({ wingPhase })}
    </svg>
  );
}

const Wings = ({ color = "#fbbf24", phase = 0 }) => {
  // phase 0 = down, 1 = up. Translates the wing tips for a flap effect.
  const lift = phase === 1 ? -10 : 4;
  return (
    <g>
      {/* Left wing */}
      <path
        d={`M 22 50 Q 5 ${50 + lift} 12 ${68 + lift / 2} Q 24 ${60 - lift / 4} 30 56 Z`}
        fill={color}
        stroke="rgba(0,0,0,0.2)"
        strokeWidth="1.5"
      />
      {/* Right wing */}
      <path
        d={`M 78 50 Q 95 ${50 + lift} 88 ${68 + lift / 2} Q 76 ${60 - lift / 4} 70 56 Z`}
        fill={color}
        stroke="rgba(0,0,0,0.2)"
        strokeWidth="1.5"
      />
    </g>
  );
};

const Eye = ({ x, y }) => (
  <g>
    <circle cx={x} cy={y} r="4.5" fill="white" />
    <circle cx={x + 1} cy={y + 0.5} r="2.5" fill="#0f172a" />
    <circle cx={x + 1.8} cy={y - 0.5} r="0.8" fill="white" />
  </g>
);

const RENDERERS = {
  cupcake: ({ wingPhase }) => (
    <g>
      <Wings color="#a78bfa" phase={wingPhase} />
      {/* Wrapper */}
      <path
        d="M 28 60 L 72 60 L 67 90 L 33 90 Z"
        fill="#7c3aed"
        stroke="#5b21b6"
        strokeWidth="1.5"
      />
      {[34, 42, 50, 58, 66].map((x, i) => (
        <line
          key={i}
          x1={x}
          y1="60"
          x2={x - 2}
          y2="90"
          stroke="rgba(0,0,0,0.25)"
          strokeWidth="1.2"
        />
      ))}
      {/* Cream */}
      <path
        d="M 26 60 Q 32 28 50 30 Q 68 28 74 60 Q 60 62 50 56 Q 40 62 26 60 Z"
        fill="#fce7f3"
        stroke="#f472b6"
        strokeWidth="1.5"
      />
      {/* Cherry */}
      <circle cx="50" cy="22" r="6" fill="#ef4444" />
      <path
        d="M 50 22 Q 53 16 56 14"
        stroke="#15803d"
        strokeWidth="1.5"
        fill="none"
      />
      {/* Sprinkles */}
      <rect x="36" y="40" width="3" height="1.5" fill="#fbbf24" rx="0.5" transform="rotate(20 36 40)" />
      <rect x="56" y="44" width="3" height="1.5" fill="#34d399" rx="0.5" transform="rotate(-30 56 44)" />
      <rect x="46" y="48" width="3" height="1.5" fill="#60a5fa" rx="0.5" />
      {/* Eyes */}
      <Eye x="42" y="68" />
      <Eye x="58" y="68" />
      {/* Smile */}
      <path d="M 44 78 Q 50 82 56 78" stroke="#0f172a" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </g>
  ),

  pizza: ({ wingPhase }) => (
    <g>
      <Wings color="#fbbf24" phase={wingPhase} />
      {/* Pizza slice */}
      <path
        d="M 50 14 L 12 84 Q 50 96 88 84 Z"
        fill="#fbbf24"
        stroke="#b45309"
        strokeWidth="2"
      />
      {/* Crust */}
      <path
        d="M 12 84 Q 50 96 88 84"
        stroke="#92400e"
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
      />
      {/* Pepperoni */}
      <circle cx="42" cy="58" r="7" fill="#dc2626" stroke="#7f1d1d" strokeWidth="1" />
      <circle cx="62" cy="64" r="6" fill="#dc2626" stroke="#7f1d1d" strokeWidth="1" />
      <circle cx="50" cy="76" r="5" fill="#dc2626" stroke="#7f1d1d" strokeWidth="1" />
      {/* Cheese drips */}
      <path d="M 30 70 Q 32 76 28 80" stroke="#fef3c7" strokeWidth="3" strokeLinecap="round" fill="none" />
      <path d="M 70 70 Q 68 76 72 80" stroke="#fef3c7" strokeWidth="3" strokeLinecap="round" fill="none" />
      {/* Eyes */}
      <Eye x="42" y="42" />
      <Eye x="58" y="42" />
      {/* Smile */}
      <path d="M 44 50 Q 50 54 56 50" stroke="#0f172a" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </g>
  ),

  coffee: ({ wingPhase }) => (
    <g>
      <Wings color="#a78bfa" phase={wingPhase} />
      {/* Cup body */}
      <rect x="26" y="38" width="48" height="48" rx="6" fill="#f97316" stroke="#9a3412" strokeWidth="2" />
      {/* Handle */}
      <path
        d="M 74 50 Q 88 50 88 62 Q 88 74 74 74"
        stroke="#9a3412"
        strokeWidth="6"
        fill="none"
      />
      {/* Coffee top */}
      <ellipse cx="50" cy="38" rx="24" ry="6" fill="#451a03" stroke="#1c0a02" strokeWidth="1.5" />
      <ellipse cx="50" cy="36" rx="20" ry="3" fill="#92400e" />
      {/* Steam */}
      <path
        d="M 38 26 Q 36 20 40 16 Q 42 20 38 26"
        fill="rgba(255,255,255,0.5)"
        stroke="rgba(255,255,255,0.8)"
        strokeWidth="1"
      />
      <path
        d="M 50 22 Q 48 16 52 12 Q 54 16 50 22"
        fill="rgba(255,255,255,0.5)"
        stroke="rgba(255,255,255,0.8)"
        strokeWidth="1"
      />
      <path
        d="M 60 26 Q 58 20 62 16 Q 64 20 60 26"
        fill="rgba(255,255,255,0.5)"
        stroke="rgba(255,255,255,0.8)"
        strokeWidth="1"
      />
      {/* Eyes */}
      <Eye x="42" y="58" />
      <Eye x="58" y="58" />
      {/* Smile */}
      <path d="M 44 70 Q 50 76 56 70" stroke="#0f172a" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    </g>
  ),

  burger: ({ wingPhase }) => (
    <g>
      <Wings color="#fbbf24" phase={wingPhase} />
      {/* Top bun */}
      <path
        d="M 18 48 Q 20 22 50 22 Q 80 22 82 48 Z"
        fill="#fbbf24"
        stroke="#b45309"
        strokeWidth="2"
      />
      {/* Sesame seeds */}
      <ellipse cx="36" cy="36" rx="2" ry="1" fill="#fef3c7" />
      <ellipse cx="50" cy="30" rx="2" ry="1" fill="#fef3c7" />
      <ellipse cx="64" cy="36" rx="2" ry="1" fill="#fef3c7" />
      {/* Lettuce */}
      <path
        d="M 16 50 Q 30 44 50 50 Q 70 44 84 50 L 82 56 L 18 56 Z"
        fill="#22c55e"
        stroke="#15803d"
        strokeWidth="1.5"
      />
      {/* Patty */}
      <rect x="18" y="56" width="64" height="10" fill="#7c2d12" stroke="#1f0a04" strokeWidth="1.5" rx="2" />
      {/* Cheese drip */}
      <path
        d="M 22 66 L 26 72 L 30 66 L 36 70 L 40 66 L 44 72 L 48 66 L 52 70 L 56 66 L 60 72 L 64 66 L 70 70 L 78 66"
        fill="#fbbf24"
        stroke="#b45309"
        strokeWidth="1"
      />
      {/* Bottom bun */}
      <path
        d="M 18 70 Q 20 90 50 90 Q 80 90 82 70 Z"
        fill="#f59e0b"
        stroke="#92400e"
        strokeWidth="2"
      />
      {/* Eyes on top bun */}
      <Eye x="42" y="38" />
      <Eye x="58" y="38" />
    </g>
  ),

  bottle: ({ wingPhase }) => (
    <g>
      <Wings color="#fbbf24" phase={wingPhase} />
      {/* Cap */}
      <rect x="42" y="6" width="16" height="12" fill="#fbbf24" stroke="#b45309" strokeWidth="1.5" rx="1" />
      {/* Neck */}
      <rect x="44" y="18" width="12" height="14" fill="#15803d" stroke="#052e16" strokeWidth="1.5" />
      {/* Body */}
      <path
        d="M 32 32 L 32 80 Q 32 90 44 90 L 56 90 Q 68 90 68 80 L 68 32 Z"
        fill="#16a34a"
        stroke="#052e16"
        strokeWidth="2"
      />
      {/* Label */}
      <rect x="36" y="48" width="28" height="22" fill="#fef3c7" stroke="#b45309" strokeWidth="1.5" rx="1.5" />
      <text
        x="50"
        y="62"
        textAnchor="middle"
        fontSize="8"
        fontWeight="bold"
        fill="#92400e"
        fontFamily="serif"
      >
        QRISE
      </text>
      {/* Reflection */}
      <rect x="36" y="36" width="3" height="44" fill="rgba(255,255,255,0.3)" rx="1.5" />
      {/* Eyes on the body above the label */}
      <Eye x="42" y="42" />
      <Eye x="58" y="42" />
    </g>
  ),
};
